import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

/**
 * Real-Time Settings Slice
 *
 * This slice stores OMS settings that must be available IMMEDIATELY when accessed,
 * bypassing the debounced BufferedDispatcher used by the main referenceData slice.
 *
 * WHY THIS EXISTS:
 * The main OMSReferenceDataSlice uses BufferedDispatcher which batches subsequent
 * dispatches every 2 seconds for performance. This causes stale data when users
 * toggle settings and immediately interact with the order form.
 *
 * WHEN TO ADD SETTINGS HERE:
 * - Settings that affect order form priming or submission
 * - Settings toggled by users who expect immediate effect
 * - Any setting where a 2-second delay would cause incorrect behavior
 *
 * HOW TO USE:
 * 1. Add the setting to this slice
 * 2. Dispatch with regular `dispatch()` in useOMSDependencies (not `dispatcher.dispatch()` which has a 2-sec debounce)
 * 3. Read from this slice in OrderSlice for time-sensitive operations
 */

export interface OMSRealTimeSettingsState {
  showDelta1BPS: boolean;
}

export const initialRealTimeSettingsState: OMSRealTimeSettingsState = {
  showDelta1BPS: false,
};

const realTimeSettingsSlice = createSlice({
  name: 'omsRealTimeSettings',
  initialState: initialRealTimeSettingsState,
  reducers: {
    setRealTimeOMSSettings: (_state, action: PayloadAction<OMSRealTimeSettingsState>) => {
      return action.payload;
    },
  },
});

export const { setRealTimeOMSSettings } = realTimeSettingsSlice.actions;
export const realTimeSettingsSliceReducer = realTimeSettingsSlice.reducer;
